/*
  ShearStore global behaviour. Dependency-free ES module, deferred by the browser.

  Configuration is read from data attributes on the element it applies to.
  There are no window globals and no order-dependent state.

  - [data-dialog-open="id"]  opens the <dialog> with that id
  - [data-dialog-close]      closes the dialog it sits in
  - <menu-disclosure>        keyboard-accessible desktop submenu
  - <announcement-rotator>   announcement bar with optional, pausable autoplay
*/

/* Dialogs ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const opener = target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-dialog-open'));
    if (dialog instanceof HTMLDialogElement) {
      // A link that opens a dialog (the cart icon) must not also navigate.
      event.preventDefault();
      if (!dialog.open) {
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
      }
    }
    return;
  }

  const closer = target.closest('[data-dialog-close]');
  if (closer) {
    const dialog = closer.closest('dialog');
    if (dialog) dialog.close();
    return;
  }

  // A click on the ::backdrop is reported with the dialog itself as the target.
  if (target instanceof HTMLDialogElement && target.hasAttribute('data-dismiss-on-backdrop')) {
    target.close();
  }
});

// The close event does not bubble, so listen during the capture phase.
document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.id) return;
    document
      .querySelectorAll(`[data-dialog-open="${CSS.escape(dialog.id)}"]`)
      .forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
  },
  true
);

/* Desktop submenu disclosure ------------------------------------------------ */

class MenuDisclosure extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.toggleButton || !this.panel) return;

    this.hoverTimer = undefined;
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.toggleButton.addEventListener('click', () => this.toggle());

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
        this.toggleButton.focus();
      }
    });

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    });

    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => {
        clearTimeout(this.hoverTimer);
        this.open();
      });
      this.addEventListener('mouseleave', () => {
        this.hoverTimer = setTimeout(() => this.close(), 150);
      });
    }

    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    clearTimeout(this.hoverTimer);
    document.removeEventListener('click', this.onDocumentClick);
  }

  get isOpen() {
    return this.toggleButton.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
  }

  close() {
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }
}

/* Announcement bar ---------------------------------------------------------- */

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-announcement-item]'));
    this.viewport = this.querySelector('[data-announcement-viewport]');
    this.index = Math.max(
      0,
      this.items.findIndex((item) => !item.hidden)
    );
    if (this.items.length < 2 || !this.viewport) return;

    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1, true));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1, true));

    // Show the announcement being edited in the theme editor.
    this.addEventListener('shopify:block:select', (event) => {
      const selected = this.items.findIndex((item) => item === event.target || item.contains(event.target));
      if (selected !== -1) this.show(selected);
    });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Number(this.dataset.interval) || 5000;
    if (this.dataset.autoplay === 'true' && !reduceMotion) {
      this.start(interval);
      // Pause while the pointer or keyboard focus is inside (WCAG 2.2.2).
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start(interval));
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start(interval));
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  show(index) {
    this.index = (index + this.items.length) % this.items.length;
    this.items.forEach((item, position) => {
      item.hidden = position !== this.index;
    });
  }

  step(direction, userInitiated) {
    // Announce changes only when the visitor asked for them.
    this.viewport.setAttribute('aria-live', userInitiated ? 'polite' : 'off');
    this.show(this.index + direction);
  }

  start(interval) {
    this.stop();
    this.timer = setInterval(() => this.step(1, false), interval);
  }

  stop() {
    clearInterval(this.timer);
  }
}

if (!customElements.get('menu-disclosure')) customElements.define('menu-disclosure', MenuDisclosure);
if (!customElements.get('announcement-rotator')) customElements.define('announcement-rotator', AnnouncementRotator);
